import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DATESTR_FMT, TDR } from 'tdr-common';
import type { DayButtonProps, MonthsProps } from 'react-day-picker';
import { DayButton, useDayPicker } from 'react-day-picker';
import ArrowLeft2 from 'iconsax-react/dist/esm/ArrowLeft2';
import ArrowRight2 from 'iconsax-react/dist/esm/ArrowRight2';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { resolveHorizonDate } from '@/helpers/horizon';
import { fromLocalJsDate, toLocalJsDate } from '../../calendar/Calendar.utils';
import {
	desktopPickerOptionBaseClassName,
	desktopPickerOptionSelectedClassName
} from './desktopParameterPickerStyles';
import { analytics, FILTER_CHANGED } from '../../../helpers/analytics';
import { useFilterAnalytics } from '../../../helpers/analytics/filterContext';

export interface DesktopOpenCalendarProps {
	restaurant: TDR.Restaurant;
	value?: string;
	onChange: (dateStr: string) => void;
}

const navButtonClassName =
	'flex size-8 items-center justify-center rounded-2xl text-white enabled:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30';

function DesktopOpenCalendarDayButton({ className, day, modifiers, ...props }: DayButtonProps) {
	const isSelected = modifiers.selected;
	const isDisabled = modifiers.disabled;

	return (
		<DayButton
			day={day}
			modifiers={modifiers}
			className={cn(
				desktopPickerOptionBaseClassName,
				'size-12 p-2',
				isSelected && desktopPickerOptionSelectedClassName,
				isDisabled && 'cursor-default opacity-50 hover:bg-[#2f3238]',
				className
			)}
			{...props}
		/>
	);
}

function DesktopOpenMonths({ children, className, ...props }: MonthsProps) {
	const { goToMonth, nextMonth, previousMonth, months } = useDayPicker();
	const { i18n } = useTranslation();
	const lang = i18n.language === 'fr' ? 'fr' : 'en';

	return (
		<div className={cn('relative !flex !w-full !flex-col gap-3 md:!flex-col', className)} {...props}>
			<div className="relative flex h-[52px] w-full shrink-0 items-center">
				<button
					type="button"
					aria-label="Previous month"
					disabled={!previousMonth}
					onClick={() => previousMonth && goToMonth(previousMonth)}
					className={cn(navButtonClassName, 'absolute left-0 top-1/2 -translate-y-1/2')}
				>
					<ArrowLeft2 size={20} />
				</button>
				<div className="flex w-full gap-[60px]">
					{months.map((calendarMonth, displayIndex) => (
						<div key={displayIndex} className="flex w-[384px] shrink-0 justify-center">
							<span className="font-poppins text-base font-semibold capitalize leading-none text-white">
								{calendarMonth.date.toLocaleString(lang, { month: 'long', year: 'numeric' })}
							</span>
						</div>
					))}
				</div>
				<button
					type="button"
					aria-label="Next month"
					disabled={!nextMonth}
					onClick={() => nextMonth && goToMonth(nextMonth)}
					className={cn(navButtonClassName, 'absolute right-0 top-1/2 -translate-y-1/2')}
				>
					<ArrowRight2 size={20} />
				</button>
			</div>
			<div className="flex w-full gap-[60px]">{children}</div>
		</div>
	);
}

export const DesktopOpenCalendar = ({ restaurant, value, onChange }: DesktopOpenCalendarProps) => {
	const { i18n } = useTranslation();
	const filterCtx = useFilterAnalytics('global explore');
	const zone = restaurant?.timezone ?? 'UTC';
	const today = DateTime.now().setZone(zone);
	const { date: horizonDate } = resolveHorizonDate({
		policies: undefined,
		restaurant,
		now: today
	});

	const horizonEnd = horizonDate.startOf('day');
	const todayStart = today.startOf('day');

	const [currentMonth, setCurrentMonth] = useState<Date>(() =>
		value
			? toLocalJsDate(DateTime.fromFormat(value, DATESTR_FMT, { zone }).startOf('month'))
			: toLocalJsDate(today.startOf('month'))
	);

	const selectedJs = value
		? toLocalJsDate(DateTime.fromFormat(value, DATESTR_FMT, { zone }))
		: undefined;

	const handleSelect = (date?: Date) => {
		if (!date) {
			return;
		}
		const luxon = fromLocalJsDate(date, restaurant?.timezone);
		const dateStr = luxon.toFormat(DATESTR_FMT);
		analytics.track(FILTER_CHANGED, {
			filterName: 'date',
			filterValue: dateStr,
			source: 'open_parameters_calendar',
			...filterCtx
		});
		onChange(dateStr);
	};

	const lang = i18n.language === 'fr' ? 'fr' : 'en';

	return (
		<Calendar
			mode="single"
			numberOfMonths={2}
			pagedNavigation
			hideNavigation
			month={currentMonth}
			onMonthChange={setCurrentMonth}
			selected={selectedJs}
			onSelect={handleSelect}
			showOutsideDays={false}
			startMonth={toLocalJsDate(todayStart)}
			endMonth={toLocalJsDate(horizonEnd)}
			components={{ Months: DesktopOpenMonths, DayButton: DesktopOpenCalendarDayButton }}
			className="w-full !bg-transparent !p-0 [--cell-size:3rem]"
			classNames={{
				root: 'w-full !p-0',
				months: '!flex !w-full !flex-col gap-3 md:!flex-col',
				month: 'flex w-[384px] shrink-0 flex-col gap-3',
				month_caption: 'hidden',
				caption_label: 'hidden',
				nav: 'hidden',
				button_previous: 'hidden',
				button_next: 'hidden',
				weekdays: 'mb-0 grid grid-cols-7 gap-2',
				weekday:
					'flex h-8 w-12 items-center justify-center font-poppins text-sm font-semibold leading-none text-[#475467]',
				weeks: 'flex flex-col gap-2',
				week: 'grid grid-cols-7 gap-2',
				day: '!aspect-auto !size-12 !p-0',
				day_button: '!size-12 !min-w-0 !p-0 !font-semibold !ring-0',
				selected: '!bg-transparent',
				disabled: '!opacity-50'
			}}
			disabled={[{ before: toLocalJsDate(todayStart) }, { after: toLocalJsDate(horizonEnd) }]}
			formatters={{
				formatWeekdayName: d =>
					d.toLocaleString(lang, { weekday: 'short' }).charAt(0).toUpperCase()
			}}
		/>
	);
};
